import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro-6";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { formatDate, getRepLevel, getRepType } from "../../../../../utils/helpers";
import { StoreType } from "../../../../../utils/types";
import TruncatedText from "../../../../full-time-shifts/ui/weekly-shift-view/truncated-text";
import {
	clearSkillAuditLog,
	setIsSkillDetailsDialogOpen,
} from "../../../../workers/store/manage-workers-slice";
import SkillRepresentationCell from "./column-skill-representations-cell";


const WorkerSkillDetailsDialog = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const isSkillDetailsDialogOpen = useSelector(
		(state: StoreType) => state.manageWorkers?.isSkillDetailsDialogOpen
	);

	const auditLogData = useSelector(
		(state: StoreType) => state.manageWorkers?.fetchWorkerSkillAuditLog.data || { auditLog: [], workerData: null, skillData: null }
	);

	const auditLogStatus = useSelector(
		(state: StoreType) => state.manageWorkers?.fetchWorkerSkillAuditLog.status
	);

	const skillLevels = useSelector(
		(state: StoreType) => state.manageWorkers?.skillMatrixFiltersSkillLevels || []
	);

	const skillExpiryEnabled = useSelector(
		(state: StoreType) => state?.user?.userData?.skillExpiryEnabled
	);

	const { auditLog, workerData, skillData } = auditLogData;

	const handleClose = () => {
		dispatch(setIsSkillDetailsDialogOpen(false));
		dispatch(clearSkillAuditLog());
	};

	const columns: GridColDef[] = useMemo(
		() => {
			const baseColumns: GridColDef[] = [
				{
					field: "skillLevel",
					headerName: t("cross-skilling_level-history"),
					minWidth: 180,
					sortable: false,
					renderCell: (params) => {
						const level = skillLevels.find(
							(l: any) => l.id === params.row.skillLevelId
						);

						if (!level) {
							return (
								<Typography variant='body2' sx={{fontStyle: "italic", color: "rgba(0,0,0,0.6)"}}>{t("cross-skilling_skill-removed")}</Typography>
							);
						}

						return (
							<Box display="flex" flexDirection="row" alignItems="center" gap={1} minWidth={0}>
								<SkillRepresentationCell
									level={level.level}
									value={level.value}
									backgroundColor={level.backColorCode}
									foregroundColor={level.foreColorCode}
									representationType={getRepType(level.repCode)}
									representationLevel={getRepLevel(level.repCode, level.level)}
									border={1}
									backgroundWidth="35px"
									backgroundHeight="35px"
								/>
								<TruncatedText
									text={level.name}
									style={{
										fontFamily: "Roboto",
										fontSize: "14px",
										fontWeight: 400,
										lineHeight: "20px",
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								/>
							</Box>
						);
					},
				},
			];

			// Add Valid From and Valid Until columns only if skill expiry is enabled
			if (skillExpiryEnabled) {
				baseColumns.push(
					{
						field: "validFrom",
						headerName: t("cross-skilling_valid-from"),
						minWidth: 110,
						sortable: false,
						renderCell: (params) => (
							<Typography variant='body2'>
								{params.row.validFrom
									? formatDate({
											dateStr: params.row.validFrom,
											formatType: "NUMERIC_DATE",
									})
									: "-"}
							</Typography>
						),
					},
					{
						field: "validTill",
						headerName: t("cross-skilling_valid-until"),
						minWidth: 110,
						sortable: false,
						renderCell: (params) => (
							<Typography variant='body2'>
								{params.row.validTill
									? formatDate({
											dateStr: params.row.validTill,
											formatType: "NUMERIC_DATE",
									})
									: "-"}
							</Typography>
						),
					}
				);
			}

			// Add Last Updated column
			baseColumns.push({
				field: "lastUpdated",
				headerName: t("cross-skilling_last-updated"),
				minWidth: 280,
				sortable: false,
				renderCell: (params) => (
					<Box minWidth={0}>
						<TruncatedText
							text={t("cross-skilling_updated-by", {
								date: formatDate({
									dateStr: params.row.dateTime,
									formatType: "NUMERIC_DATE",
								}),
								name: params.row.changedByName,
							})}
							style={{
								fontFamily: "Roboto",
								fontSize: "14px",
								fontWeight: 400,
								lineHeight: "20px",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
						/>
					</Box>
				),
			});

			return baseColumns;
		},
		[skillLevels, skillExpiryEnabled, t]
	);

	const rows = useMemo(
		() =>
			auditLog.map((log: any, index: number) => ({
				id: index,
				action: log.action,
				skillLevelId: log.skillLevelId,
				validFrom: log.skillValidityFrom,
				validTill: log.skillValidityTo,
				dateTime: log.dateTime,
				changedByName: log.changedByName,
			})),
		[auditLog]
	);

	return (
		<>
			<Dialog
				open={isSkillDetailsDialogOpen}
				onClose={handleClose}
				aria-describedby='worker-skill-details-dialog'
				sx={{
					"& .MuiDialog-paper": {
						width: skillExpiryEnabled ? "790px" : "600px",
						maxWidth: "90vw",
					},
				}}
			>
				<DialogTitle sx={{ padding: "45px 45px 16px" }}>
					{t("cross-skilling_worker-skill-details")}
				</DialogTitle>
				<DialogContent
					sx={{
						padding: "0px 45px",
					}}
				>
					{auditLogStatus === "pending" ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: "178px",
							}}
						>
							<CircularProgress />
						</Box>
					) : (
						<>
							{/* Worker and Skill Names */}
							<Box
								sx={{
									display: "flex",
									gap: 3,
									mb: 1,
									alignItems: "center",
									overflow: "hidden",
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, flex: "0 1 auto", maxWidth: "50%" }}>
									<PersonOutlineOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
									<TruncatedText
										text={workerData ? `${workerData.firstName} ${workerData.lastName}` : t("cross-skilling_worker")}
										style={{
											fontFamily: "Roboto",
											fontSize: "14px",
											fontWeight: 400,
											lineHeight: "20px",
											color: "#1C1B1F",
											width: "100%",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									/>
								</Box>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, flex: "0 1 auto", maxWidth: "50%" }}>
									<BuildOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
									<TruncatedText
										text={skillData?.skillName || skillData?.name || t("cross-skilling_skill")}
										style={{
											fontFamily: "Roboto",
											fontSize: "14px",
											fontWeight: 400,
											lineHeight: "20px",
											color: "#1C1B1F",
											width: "100%",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									/>
								</Box>
							</Box>

							{/* DataGrid */}
							<Box sx={{ width: "100%", height: "200px", mt: 2 }}>
								<DataGridPro
									rows={rows}
									columns={columns}
									hideFooter
									disableColumnMenu
									disableColumnFilter
									disableColumnSelector
									disableColumnReorder
									isRowSelectable={() => false}
									rowHeight={50}
									sx={{
										border: "none",
										"& .MuiDataGrid-columnHeader:focus-within": {
											outline: "none",
										},
										"& .MuiDataGrid-cell:focus-within": {
											outline: "none",
										},
									}}
									localeText={{
										noRowsLabel: t("cross-skilling_no-history-record"),
									}}
								/>
							</Box>
						</>
					)}
				</DialogContent>
				<DialogActions
					sx={{
						padding: "33px 45px",
					}}
				>
					<Box
						sx={{
							display: "flex",
							gap: "12px",
						}}
					>
						<Box>
							<Button onClick={handleClose} variant='contained'>
								{t("common_close")}
							</Button>
						</Box>
					</Box>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default WorkerSkillDetailsDialog;
